import { DeviceParts } from "@cva/renderer";
import {
  clipContentAspect,
  clipDeviceProps,
  deviceClipScreen,
  hiddenTracks,
  layoutClips,
  mainPersonClips,
  type OverlayClip,
  overlayClips,
  paintPreviewColor,
  personLift,
  stackItems,
  stackSlot,
  type VideoStack,
  videoDuration,
} from "@cva/shared";
import { Fragment, memo, useCallback, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import type { AssetInfo } from "../commands";
import { useEnv } from "../env";
import { useEditor } from "../store";
import { useCaptions } from "./data";
import { CompositionPlayer, type DeviceTarget, MusicPlayer } from "./player";
import { useVideoApi } from "./store";

const CANVAS_STYLE = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
} as const;

/**
 * The video picture below the overlays (SceneRenderer underlay). Owns the
 * players; the video store tells it to play, pause or seek, it reports the
 * time back while playing. Clips in a device mockup get their device as DOM
 * between the canvases (see player.ts). The clips of the tracks above a
 * band of elements go into that band's host (`hosts[slot - 1]`, the page's
 * interlays, video/tracks.ts).
 */
export const VideoLayer = memo(function VideoLayer({
  stack,
  hosts,
}: {
  stack: VideoStack;
  hosts: readonly (HTMLElement | null)[];
}) {
  const video = useVideoApi()!;
  const env = useEnv();
  const timeline = useEditor((s) => s.doc.timeline);
  const elements = useEditor((s) => s.doc.elements);
  const frame = useEditor((s) => s.doc.format);
  const pageBackground = useEditor((s) => s.doc.pages[0]?.background ?? "#000000");
  const { speech } = useCaptions();
  const root = useRef<HTMLDivElement>(null);
  // The <video> elements of the player live here: decoding and sound only.
  const host = useRef<HTMLDivElement>(null);
  const screen = useRef<HTMLCanvasElement>(null);
  const slotCanvases = useRef<(HTMLCanvasElement | null)[]>([]);
  const player = useRef<CompositionPlayer | null>(null);
  const music = useRef<MusicPlayer | null>(null);

  const layout = useMemo(() => layoutClips(timeline), [timeline]);
  // Hidden tracks are not played.
  const overlays = useMemo(() => {
    const hidden = hiddenTracks({ timeline });
    return overlayClips(timeline).filter((clip) => !hidden.has(clip.track));
  }, [timeline]);
  const devices = useMemo(
    () =>
      overlays.filter((clip) => clip.device).sort((a, b) => a.track - b.track || a.start - b.start),
    [overlays],
  );
  const persons = useMemo(() => mainPersonClips(timeline, frame), [timeline, frame]);
  const liftJson = JSON.stringify(personLift({ timeline }));
  const lift = useMemo(
    () => JSON.parse(liftJson) as { split: number; track: number } | null,
    [liftJson],
  );
  /** The slot of the stack a device lies in (its DOM goes there). */
  const slotOf = useCallback(
    (clip: OverlayClip) => stackSlot(stack, stackItems([clip], [], lift)[0]?.key ?? 0),
    [stack, lift],
  );
  const duration = useMemo(() => videoDuration({ timeline, elements }), [timeline, elements]);
  const background = paintPreviewColor(pageBackground);

  useEffect(() => {
    const musicPlayer = new MusicPlayer();
    const main = new CompositionPlayer(
      host.current!,
      screen.current!,
      (t) => {
        video.getState().tick(t);
        musicPlayer.sync(t, true);
      },
      () => video.getState().pause(),
    );
    player.current = main;
    music.current = musicPlayer;
    const unsubscribe = video.subscribe((state, previous) => {
      if (state.playing !== previous.playing) {
        if (state.playing) {
          main.play(state.time);
        } else {
          main.pause();
          musicPlayer.sync(state.time, false);
        }
      } else if (state.seekVersion !== previous.seekVersion) {
        main.showAt(state.time);
        musicPlayer.sync(state.time, state.playing);
      }
    });
    return () => {
      unsubscribe();
      main.destroy();
      musicPlayer.destroy();
      player.current = null;
      music.current = null;
    };
  }, [video]);

  useEffect(() => {
    // The devices are rendered by now: hand their elements to the player.
    const targets: DeviceTarget[] = [];
    for (const clip of devices) {
      const slot = slotOf(clip);
      const host = slot === 0 ? root.current : (hosts[slot - 1] ?? null);
      const wrapper = host?.querySelector<HTMLElement>(
        `[data-device-clip="${CSS.escape(clip.id)}"]`,
      );
      const layer = host?.querySelector<HTMLCanvasElement>(
        `[data-device-layer="${CSS.escape(clip.id)}"]`,
      );
      if (!wrapper || !layer) continue;
      const quad = deviceClipScreen(clip, env.assets.get(clip.assetId));
      targets.push({
        clip,
        wrapper,
        layer,
        screen: wrapper.querySelector<HTMLCanvasElement>("canvas[data-device-canvas]"),
        screenSize: quad ? { width: quad.width, height: quad.height } : null,
      });
    }
    player.current?.setData({
      layout,
      overlays,
      assets: env.assets,
      frame,
      duration,
      background,
      devices: targets,
      persons,
      stack,
      lift,
      slots: hosts.map((_, index) => slotCanvases.current[index] ?? null),
    });
    music.current?.setData(timeline?.audio ?? [], env.assets, speech, duration);
    const state = video.getState();
    if (!state.playing) music.current?.sync(state.time, false);
    // Keep the playhead inside the (possibly shorter) video.
    if (state.time > duration) state.seek(duration);
  }, [
    layout,
    overlays,
    devices,
    env.assets,
    frame,
    duration,
    background,
    timeline?.audio,
    speech,
    video,
    persons,
    stack,
    hosts,
    slotOf,
    lift,
  ]);

  return (
    <div
      ref={root}
      style={{ position: "absolute", inset: 0, overflow: "hidden" }}
      data-video-layer=""
    >
      <div ref={host} style={{ position: "absolute", left: 0, top: 0, width: 2, height: 2 }} />
      <canvas ref={screen} style={CANVAS_STYLE} />
      {devices
        .filter((clip) => slotOf(clip) === 0)
        .map((clip) => (
          <Fragment key={clip.id}>
            <DeviceClipFrame clip={clip} asset={env.assets.get(clip.assetId)} />
            <canvas data-device-layer={clip.id} style={CANVAS_STYLE} />
          </Fragment>
        ))}
      {hosts.map((host, index) =>
        host
          ? createPortal(
              <>
                <canvas
                  ref={(canvas) => {
                    slotCanvases.current[index] = canvas;
                  }}
                  style={CANVAS_STYLE}
                  data-video-slot={index + 1}
                />
                {devices
                  .filter((clip) => slotOf(clip) === index + 1)
                  .map((clip) => (
                    <Fragment key={clip.id}>
                      <DeviceClipFrame clip={clip} asset={env.assets.get(clip.assetId)} />
                      <canvas data-device-layer={clip.id} style={CANVAS_STYLE} />
                    </Fragment>
                  ))}
              </>,
              host,
              `slot-${index}`,
            )
          : null,
      )}
    </div>
  );
});

/** The device of a clip on its box; the player paints the picture onto its screen canvas. */
const DeviceClipFrame = memo(function DeviceClipFrame({
  clip,
  asset,
}: {
  clip: OverlayClip;
  asset: AssetInfo | undefined;
}) {
  const device = clip.device;
  if (!device) return null;
  return (
    <div
      data-device-clip={clip.id}
      style={{
        position: "absolute",
        left: clip.box.x,
        top: clip.box.y,
        width: clip.box.width,
        height: clip.box.height,
        visibility: "hidden",
        pointerEvents: "none",
      }}
    >
      <DeviceParts
        element={{
          ...clipDeviceProps(device),
          id: clip.id,
          width: clip.box.width,
          height: clip.box.height,
          tilt: device.tilt,
          glare: device.glare,
        }}
        asset={undefined}
        contentAspect={clipContentAspect(asset)}
        idPrefix="video-device"
        screen={
          <canvas
            data-device-canvas=""
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              display: "block",
            }}
          />
        }
      />
    </div>
  );
});
